import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Badge } from '../../components/Badge';
import { Modal } from '../../components/Modal';
import { soundEngine } from '../../lib/sounds';
import { haptics } from '../../lib/haptics';
import {
  Check,
  Copy,
  KeyRound,
  Monitor,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserPlus,
  Download,
  QrCode,
} from 'lucide-react';
import QRCode from 'qrcode';
import { Capacitor } from '@capacitor/core';
import { DOWNLOAD_LINKS } from '../../lib/downloadLinks';

export function UsersPage() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Add User Modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newFullName, setNewFullName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newDepartment, setNewDepartment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Enrollment Code Modal
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [enrollmentCode, setEnrollmentCode] = useState<string | null>(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState<string | null>(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedJson, setCopiedJson] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');

  const fetchUsers = async () => {
    try {
      const res = await api.admin.getUsers();
      setUsers(res.users);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.admin.createUser({
        fullName: newFullName,
        email: newEmail,
        department: newDepartment || undefined,
      });
      soundEngine.playSuccess();
      haptics.notificationSuccess();
      setAddModalOpen(false);
      setNewFullName('');
      setNewEmail('');
      setNewDepartment('');
      await fetchUsers();
    } catch (err: any) {
      soundEngine.playError();
      haptics.notificationError();
      alert(err.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEnroll = async (user: any) => {
    soundEngine.playTap();
    haptics.impactLight();
    setSelectedUser(user);
    setEnrollmentCode(null);
    setCopied(false);
    setEnrollModalOpen(true);
    setGeneratingCode(true);

    try {
      const res = await api.admin.generateEnrollmentToken(user.id);
      setEnrollmentCode(res.enrollmentToken.code);
      setCodeExpiresAt(res.enrollmentToken.expiresAt);

      const serverOrigin =
        Capacitor.isNativePlatform() || window.location.hostname === 'localhost' || window.location.protocol === 'capacitor:'
          ? 'https://ck-pulse.pages.dev'
          : window.location.origin;

      const qrPayload = JSON.stringify({
        server: serverOrigin,
        code: res.enrollmentToken.code,
        name: `${user.fullName?.split(' ')[0] || 'Office'}-PC`,
        user: user.fullName,
      });

      const qrUrl = await QRCode.toDataURL(qrPayload, {
        width: 260,
        margin: 1.5,
        color: { dark: '#000000', light: '#FFFFFF' },
      });
      setQrDataUrl(qrUrl);

      soundEngine.playPop();
    } catch (err: any) {
      soundEngine.playError();
      alert(err.message || 'Failed to generate workstation enrollment code');
      setEnrollModalOpen(false);
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopyCode = () => {
    if (!enrollmentCode) return;
    navigator.clipboard.writeText(enrollmentCode);
    soundEngine.playPop();
    haptics.notificationSuccess();
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleCopyJson = () => {
    if (!enrollmentCode || !selectedUser) return;
    const serverOrigin =
      Capacitor.isNativePlatform() || window.location.hostname === 'localhost' || window.location.protocol === 'capacitor:'
        ? 'https://ck-pulse.pages.dev'
        : window.location.origin;

    const payload = JSON.stringify({
      server: serverOrigin,
      code: enrollmentCode,
      name: `${selectedUser.fullName?.split(' ')[0] || 'Office'}-PC`,
      user: selectedUser.fullName,
    }, null, 2);
    navigator.clipboard.writeText(payload);
    soundEngine.playPop();
    haptics.notificationSuccess();
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 2500);
  };

  const handleDeleteUser = async (user: any) => {
    if (!confirm(`Are you sure you want to remove user "${user.fullName}"?`)) return;
    try {
      await api.admin.deleteUser(user.id);
      soundEngine.playPop();
      haptics.impactMedium();
      await fetchUsers();
    } catch (err: any) {
      soundEngine.playError();
      alert(err.message || 'Failed to delete user');
    }
  };

  const filteredUsers = (users || []).filter(
    (u) =>
      (u?.fullName || '').toLowerCase().includes(search.toLowerCase()) ||
      (u?.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (u?.department && u.department.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-5 sm:space-y-8 max-w-7xl mx-auto w-full min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900 dark:text-white tracking-tight">Staff & Users</h1>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
            Manage monitored employees, departments, and enrollment tokens
          </p>
        </div>

        <button
          onClick={() => {
            soundEngine.playTap();
            haptics.impactLight();
            setAddModalOpen(true);
          }}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white shadow-md shadow-blue-500/20 transition-all active:scale-[0.97] self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Add User</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm w-full">
          <Search className="w-4 h-4 absolute left-3.5 top-2.5 text-zinc-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users or departments..."
            className="w-full pl-9 pr-3 py-2 text-xs rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:border-pulse-blue shadow-sm"
          />
        </div>
      </div>

      {/* Mobile Users Cards (Visible on mobile, hidden on md+) */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 bg-white/80 dark:bg-[#0E0E0E] rounded-2xl border border-zinc-200 dark:border-zinc-800 text-xs shadow-sm">
            Loading users...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 bg-white/80 dark:bg-[#0E0E0E] rounded-2xl border border-zinc-200 dark:border-zinc-800 text-xs shadow-sm">
            No users matching criteria.
          </div>
        ) : (
          filteredUsers.map((u) => (
            <div key={u.id} className="p-4 rounded-2xl border border-zinc-200/80 dark:border-zinc-800 bg-white/80 dark:bg-[#0E0E0E] space-y-3 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-zinc-900 dark:text-white text-sm">{u.fullName}</div>
                  <div className="text-[11px] text-zinc-500 dark:text-zinc-400">{u.email}</div>
                  <span className="inline-block mt-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700">
                    {u.department || 'General'}
                  </span>
                </div>
                {u.presence && <Badge status={u.presence.status} />}
              </div>

              <div className="text-xs pt-2 border-t border-zinc-100 dark:border-zinc-800/80 flex items-center justify-between">
                <span className="text-[11px] text-zinc-500">Workstation:</span>
                <span className="font-mono text-zinc-700 dark:text-zinc-300 text-[11px]">
                  {u.device ? (u.device.deviceName || u.device.hostname) : <span className="italic text-zinc-400">None</span>}
                </span>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-zinc-100 dark:border-zinc-800/80 gap-2">
                <Link
                  to={`/admin/users/${u.id}`}
                  onClick={() => {
                    soundEngine.playTap();
                    haptics.impactLight();
                  }}
                  className="px-3 py-1.5 text-xs rounded-xl bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 transition active:scale-95"
                >
                  View Profile
                </Link>
                <button
                  onClick={() => handleOpenEnroll(u)}
                  className="px-3 py-1.5 text-xs rounded-xl text-blue-600 dark:text-pulse-blue hover:bg-blue-500/10 border border-blue-500/20 transition active:scale-95 font-medium"
                >
                  Enroll PC
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Users Table (Hidden on mobile, visible on md+) */}
      <div className="hidden md:block rounded-2xl border border-zinc-200/80 dark:border-zinc-800 bg-white/90 dark:bg-[#0E0E0E] overflow-hidden shadow-sm dark:shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-zinc-100/80 dark:bg-zinc-900/90 text-zinc-600 dark:text-zinc-400 uppercase tracking-wider font-medium border-b border-zinc-200/80 dark:border-zinc-800">
              <tr>
                <th className="py-3.5 px-4">User</th>
                <th className="py-3.5 px-4">Department</th>
                <th className="py-3.5 px-4">Assigned Workstation</th>
                <th className="py-3.5 px-4">Presence Status</th>
                <th className="py-3.5 px-4">Last Activity</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200/60 dark:divide-zinc-800/60 text-zinc-700 dark:text-zinc-300">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-zinc-500">
                    Loading users...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-zinc-500">
                    No users matching criteria.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-900/40 transition">
                    <td className="py-3.5 px-4 font-medium text-zinc-900 dark:text-white">
                      <div>{u.fullName}</div>
                      <div className="text-[11px] text-zinc-500">{u.email}</div>
                    </td>
                    <td className="py-3.5 px-4 text-zinc-600 dark:text-zinc-400">{u.department || 'General'}</td>
                    <td className="py-3.5 px-4">
                      {u.device ? (
                        <div className="flex items-center gap-1.5 font-mono text-zinc-700 dark:text-zinc-300">
                          <Monitor className="w-3.5 h-3.5 text-zinc-400" />
                          <span>{u.device.deviceName || u.device.hostname}</span>
                        </div>
                      ) : (
                        <span className="text-zinc-400 italic">No device paired</span>
                      )}
                    </td>
                    <td className="py-3.5 px-4">
                      <Badge status={u.disabled ? 'DISABLED' : (u.device?.status || 'PENDING')} />
                    </td>
                    <td className="py-3.5 px-4 text-zinc-600 dark:text-zinc-400">
                      {u.device?.lastActivityAt
                        ? new Date(u.device.lastActivityAt).toLocaleTimeString()
                        : u.lastActivityAt
                        ? new Date(u.lastActivityAt).toLocaleTimeString()
                        : '—'}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenEnroll(u)}
                          title="Generate device enrollment code"
                          className="px-2.5 py-1 text-xs rounded-lg text-blue-600 dark:text-pulse-blue hover:bg-blue-500/10 border border-blue-500/20 transition active:scale-95 inline-flex items-center gap-1 font-medium"
                        >
                          <KeyRound className="w-3 h-3" />
                          <span>Pair Agent</span>
                        </button>
                        {u.role !== 'ADMIN' && (
                          <button
                            onClick={() => handleDeleteUser(u)}
                            className="p-1 text-zinc-400 hover:text-rose-500 transition active:scale-95"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      <Modal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Add Workstation User"
        description="Create a user profile to enable device enrollment and presence monitoring."
      >
        <form onSubmit={handleCreateUser} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-zinc-300 mb-1">Full Name</label>
            <input
              type="text"
              required
              value={newFullName}
              onChange={(e) => setNewFullName(e.target.value)}
              placeholder="e.g. Rahul Patel"
              className="w-full p-2.5 text-xs rounded-xl pulse-input focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-300 mb-1">Email Address</label>
            <input
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="e.g. rahul@company.com"
              className="w-full p-2.5 text-xs rounded-xl pulse-input focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-300 mb-1">Department (Optional)</label>
            <input
              type="text"
              value={newDepartment}
              onChange={(e) => setNewDepartment(e.target.value)}
              placeholder="e.g. Operations, Logistics, IT"
              className="w-full p-2.5 text-xs rounded-xl pulse-input focus:outline-none"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#222222]">
            <button
              type="button"
              onClick={() => setAddModalOpen(false)}
              className="px-3 py-2 text-xs font-medium rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-xs font-medium rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white transition-all disabled:opacity-50 flex items-center gap-2"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>{submitting ? 'Creating...' : 'Create User'}</span>
            </button>
          </div>
        </form>
      </Modal>

      {/* Device Enrollment Modal */}
      <Modal
        isOpen={enrollModalOpen}
        onClose={() => setEnrollModalOpen(false)}
        title={`Enroll Workstation for ${selectedUser?.fullName}`}
        description="One-time pairing code for registering the CK Pulse Windows Agent."
        maxWidth="max-w-lg"
      >
        <div className="space-y-4">
          {generatingCode ? (
            <div className="py-8 text-center text-xs text-zinc-400 flex flex-col items-center gap-2">
              <div className="w-5 h-5 border-2 border-pulse-blue border-t-transparent rounded-full animate-spin" />
              <span>Generating cryptographically secure pairing token...</span>
            </div>
          ) : (
            <>
              {/* Dynamic QR Code & Code Block */}
              <div className="p-4 rounded-2xl bg-zinc-950 border border-zinc-800 flex flex-col sm:flex-row items-center gap-4">
                {qrDataUrl && (
                  <div className="p-2.5 rounded-2xl bg-white shrink-0 shadow-lg border border-black/10">
                    <img src={qrDataUrl} alt="Enrollment QR" className="w-28 h-28 object-contain" />
                  </div>
                )}
                <div className="flex-1 min-w-0 text-center sm:text-left">
                  <span className="text-[10px] uppercase font-bold text-zinc-500 tracking-wider block mb-1">
                    One-Time Code & Dynamic QR
                  </span>
                  <span className="font-mono text-xl font-bold text-white tracking-widest selection:bg-pulse-blue block">
                    {enrollmentCode}
                  </span>
                  <p className="text-[11px] text-zinc-400 mt-1">
                    Scan with webcam or click below to copy setup data for Windows Agent.
                  </p>

                  <div className="flex items-center gap-2 mt-3 justify-center sm:justify-start">
                    <button
                      onClick={handleCopyCode}
                      className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors ios-press"
                    >
                      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copied ? 'Code Copied!' : 'Copy Code'}</span>
                    </button>

                    <button
                      onClick={handleCopyJson}
                      className="px-3 py-1.5 rounded-lg bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 border border-blue-500/40 text-xs font-semibold flex items-center gap-1.5 transition-colors ios-press"
                    >
                      {copiedJson ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <QrCode className="w-3.5 h-3.5" />}
                      <span>{copiedJson ? 'Payload Copied!' : 'Copy 1-Click JSON'}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="p-4 rounded-xl bg-[#141414] border border-[#222222] text-xs text-zinc-300 space-y-2">
                <div className="font-semibold text-white flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <span>Workstation Setup Instructions:</span>
                </div>
                <ol className="list-decimal list-inside space-y-1 text-zinc-400 pl-1">
                  <li>Launch the CK Pulse Agent on the user's Windows PC.</li>
                  <li>Enter or paste this enrollment code when prompted.</li>
                  <li>The workstation will immediately link and begin local presence monitoring.</li>
                </ol>
                <p className="text-[11px] text-zinc-500 pt-1">
                  Code expires in 24 hours and is permanently invalidated after enrollment.
                </p>

                <div className="pt-2 flex flex-col gap-2 border-t border-[#222222]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <a
                        href={DOWNLOAD_LINKS.WINDOWS_EXE}
                        download="CKPulse.Agent.exe"
                        className="inline-flex items-center gap-1.5 text-xs text-pulse-blue hover:text-white transition-colors font-medium"
                        title="Download trusted Windows Agent (.exe)"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>Download Agent (.exe)</span>
                      </a>
                      <span className="text-zinc-600">•</span>
                      <a
                        href={DOWNLOAD_LINKS.WINDOWS_ZIP}
                        download="CKPulse-Agent-v1.0.0-win-x64.zip"
                        className="text-xs text-zinc-400 hover:text-white transition-colors"
                        title="Download recommended .ZIP package (no browser warning)"
                      >
                        .ZIP Bundle (Recommended)
                      </a>
                    </div>
                    <span className="text-[10px] text-zinc-500 font-mono">v1.0.0</span>
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    <span>Requires .NET 9 Desktop: </span>
                    <a
                      href="https://aka.ms/dotnet/9.0/windowsdesktop-runtime-win-x64.exe"
                      target="_blank"
                      rel="noreferrer"
                      className="text-pulse-blue hover:underline"
                    >
                      Microsoft Official Installer (60 MB)
                    </a>
                  </div>
                </div>
              </div>

              <div className="text-right pt-2 border-t border-[#222222]">
                <button
                  onClick={() => setEnrollModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white transition-colors"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
